import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Award, CalendarPlus, ClipboardCheck, Mail, Phone, PhoneMissed, PhoneOff, Tag } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  CONTACT_FILTERS,
  CONTACT_FILTER_LABELS,
  CONTACT_STATUS_LABELS,
  CONTACT_WITHDRAWAL_STATUSES,
  contactMovesFrom,
  isOpenContactStatus,
  matchesContactFilter,
  type ContactFilter,
  type ContactManualStatus,
  type ContactRow,
  type ContactTrackerView,
  type JobDetail,
} from '@nest-recruit/shared';
import { Avatar } from '../../../components/Avatar';
import { Button } from '../../../components/Button';
import { Chip } from '../../../components/Chip';
import { DataTable } from '../../../components/data-table/DataTable';
import { ModuleGate } from '../../../components/ModuleGate';
import type { Column } from '../../../components/data-table/types';
import { MenuItem, Popover } from '../../../components/Popover';
import { CONTACT_STATUS_TONES } from '../../../components/status-tones';
import { EmptyState } from '../../../components/system-states/SystemState';
import { toneCardClass } from '../../../components/Tone';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { errorMessage } from '../../../lib/api-client';
import { formatDateTime, formatRelative } from '../../../lib/format';
import { EMPTY_SELECTION, isSelected, type SelectionState } from '../../../lib/selection';
import { useToasts } from '../../../stores/toasts';
import { BookSessionPanel } from '../../assessments/components/BookSessionPanel';
import { CandidatePopup, type CandidatePanelTab } from '../../candidates/components/panel/CandidatePanel';
import { SessionEditor } from '../../assessments/components/SessionEditor';
import { WithdrawalPicker } from '../../candidates/components/WithdrawalPicker';
import { ComposePanel } from '../../communication/components/ComposePanel';
import { startBulkAction } from '../../pipeline/api/pipeline-api';
import { selectionApi } from '../../comparison/api/comparison-api';
import { contactKey, useAddPlannerRow, useLogCall, useMoveToAssessment, useSetContactStatus } from '../api/contact-api';
import styles from './contact.module.css';
import fit from '../../../components/FitPage.module.css';
import { nextStepKind, trackerTestAction } from './tracker-test-action';

// R7 — CON-01, the contact tracker (§L32, F24.06, F24.07). It answers the owner's three
// questions with one filter each (O34, DECISIONS-LOG "Start R7" Q3): who still needs contacting,
// who isn't answering, who confirmed, and who isn't coming. A call's outcome is one tap (CT-3);
// on a phone the number dials (`tel:`). R8: "Send assessment" (F24.10) moves the Confirmed to
// ⑦ Assessment and emails each their link, in one press (DECISIONS-LOG "Start R8" Q2); a test sat
// on paper books them on an exam session instead (`trackerTestAction`).

/** What the tracker needs of the job — the job page's own `JobDetail` is one. */
interface TrackerJob {
  readonly id: JobDetail['id'];
  readonly assessment: { readonly test: { readonly isOffline: boolean } | null };
}

interface TrackerProps {
  readonly detail: TrackerJob;
  readonly view: ContactTrackerView;
  readonly filter: ContactFilter;
  readonly onFilter: (filter: ContactFilter) => void;
  readonly zone: string;
  /** The person whose pop-up is open, by application (kept in the address, 06-qol §6). */
  readonly openApplicationId: string | null;
  readonly onOpenCandidate: (applicationId: string | null) => void;
}

type Popped =
  | { readonly kind: 'status'; readonly row: ContactRow; readonly anchor: AnchorRect }
  | { readonly kind: 'withdraw'; readonly row: ContactRow; readonly to: ContactManualStatus; readonly anchor: AnchorRect }
  | { readonly kind: 'email'; readonly anchor: AnchorRect }
  | { readonly kind: 'exam'; readonly anchor: AnchorRect };

export function ContactTracker({ detail, view, filter, onFilter, zone, openApplicationId, onOpenCandidate }: TrackerProps): ReactNode {
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const [panelTab, setPanelTab] = useState<CandidatePanelTab>('cv');
  const [popped, setPopped] = useState<Popped | null>(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const { set, pending: setting } = useSetContactStatus(detail.id);
  const { log, pending: logging } = useLogCall(detail.id);
  const moveToAssessment = useMoveToAssessment(detail.id);
  const { add: addPlannerRow } = useAddPlannerRow(detail.id);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const rows = view.rows.filter((row) => matchesContactFilter(row, filter));
  const selected = view.rows.filter((row) => isSelected(selection, row.applicationId));
  const isPending = (id: string): boolean => setting.has(id) || logging.has(id);
  const close = (): void => setPopped(null);

  async function emailSelected(draft: { readonly templateKey: string | null; readonly subject: string; readonly body: string }): Promise<void> {
    const people = selected.length;
    close();
    setSelection(EMPTY_SELECTION);
    try {
      // F24.07: the pipeline's own bulk "Send message", one email each with their own name and job.
      await startBulkAction(detail.id, { action: 'message', selection: { kind: 'ids', ids: selected.map((row) => row.applicationId) }, templateKey: draft.templateKey, subject: draft.subject, body: draft.body });
      show({ tone: 'success', message: `Emailing ${people} ${people === 1 ? 'person' : 'people'}`, detail: draft.templateKey ? 'They show as Emailed as each one goes.' : 'A message written by hand leaves their status as it is.' });
    } catch (error) {
      show({ tone: 'danger', message: 'The emails didn’t go', detail: errorMessage(error) });
    }
  }

  // F24.10: only people who said yes to the next step, and are still at Contact.
  const toTest = selected.filter((row) => row.contactStatus === 'confirmed' && row.stage === 'screening');

  async function sendAssessment(): Promise<void> {
    const people = toTest.length;
    const leftOut = selected.length - people;
    setSelection(EMPTY_SELECTION);
    try {
      // The pipeline's own "Assign assessment" (S17 Q4): into Assessment, then the link — sent once.
      // A test taken outside the app sends nothing; the move is the whole step (ADR 0047).
      await moveToAssessment({ action: 'assign_assessment', selection: { kind: 'ids', ids: toTest.map((row) => row.applicationId) } });
      const leftOutNote = leftOut > 0 ? ` ${leftOut} not confirmed ${leftOut === 1 ? 'was' : 'were'} left out.` : '';
      show(
        testAction === 'move_only'
          ? { tone: 'success', message: `Moving ${people} ${people === 1 ? 'person' : 'people'} to Assessment`, detail: `Record each result there once they have taken the test.${leftOutNote}` }
          : { tone: 'success', message: `Sending the test to ${people} ${people === 1 ? 'person' : 'people'}`, detail: `They move to Assessment, and each gets a personal link with a deadline.${leftOutNote}` },
      );
    } catch (error) {
      show({ tone: 'danger', message: 'The test wasn’t sent', detail: errorMessage(error) });
    }
  }

  const testAction = trackerTestAction(view, detail.assessment.test?.isOffline ?? false);
  const nextKind = nextStepKind(view, testAction);

  // Owner, 2026-11-01 (§L32 "Send to the next step"): one click confirms the person if they aren't yet,
  // then sends them to whichever step is next, by the same calls the bulk buttons and the planner use.
  async function sendOn(row: ContactRow): Promise<void> {
    if (!nextKind) return;
    setSendingId(row.applicationId);
    try {
      if (row.contactStatus !== 'confirmed') {
        const isConfirmed = await set(row, { to: 'confirmed', note: `Confirmed when sent to ${view.nextStepLabel}.` });
        if (!isConfirmed) return;
      }
      // `set` waits for the refetch, so the row's version here is the one the move must carry.
      const fresh = queryClient.getQueryData<ContactTrackerView>(contactKey(detail.id))?.rows.find((candidate) => candidate.applicationId === row.applicationId) ?? row;
      if (nextKind === 'assessment') {
        await moveToAssessment({ action: 'assign_assessment', selection: { kind: 'ids', ids: [row.applicationId] } });
        show({
          tone: 'success',
          message: testAction === 'move_only' ? `${row.candidateName} moved to Assessment` : `Test sent to ${row.candidateName}`,
          detail: testAction === 'move_only' ? 'Record the result there once they have taken the test.' : 'They move to Assessment and get a personal link with a deadline.',
        });
      } else if (nextKind === 'select') {
        await selectionApi.select(detail.id, row.applicationId, fresh.version);
        await Promise.all([queryClient.invalidateQueries({ queryKey: ['applications'] }), queryClient.invalidateQueries({ queryKey: ['comparison'] })]);
        show({ tone: 'success', message: `${row.candidateName} selected`, detail: 'The recruiter, the requester and the line manager have been told.' });
      } else {
        const interviewId = await addPlannerRow({ applicationId: row.applicationId, version: fresh.version, candidateName: row.candidateName });
        if (interviewId) show({ tone: 'success', message: `${row.candidateName} moved to ${view.nextStepLabel}`, detail: 'Set the time and the panel in the Planner tab.' });
      }
    } catch (error) {
      show({ tone: 'danger', message: `${row.candidateName} wasn’t sent on`, detail: errorMessage(error) });
    } finally {
      setSendingId(null);
    }
  }

  // Evening review 2026-11-01: on a job whose next step is an interview, the ticked Confirmed people go to the first round
  // together, each by the planner's own "+ Add row" (which says why, per person, when one is refused).
  async function sendConfirmedToRound(): Promise<void> {
    const people = toTest;
    setSelection(EMPTY_SELECTION);
    let done = 0;
    for (const row of people) {
      if (await addPlannerRow({ applicationId: row.applicationId, version: row.version, candidateName: row.candidateName })) done += 1;
    }
    if (done > 0) show({ tone: 'success', message: `${done} ${done === 1 ? 'person' : 'people'} moved to ${view.nextStepLabel}`, detail: 'Set the times and the panel in the Planner tab.' });
  }

  // C4-a (ADR 0077): no test and no interview — Confirmed people are selected from here, as a last round's pass would be.
  async function selectConfirmed(): Promise<void> {
    const people = toTest;
    setSelection(EMPTY_SELECTION);
    const failed: string[] = [];
    for (const row of people) {
      try {
        await selectionApi.select(detail.id, row.applicationId, row.version);
      } catch (error) {
        failed.push(`${row.candidateName}: ${errorMessage(error)}`);
      }
    }
    await Promise.all([queryClient.invalidateQueries({ queryKey: ['applications'] }), queryClient.invalidateQueries({ queryKey: ['comparison'] })]);
    const done = people.length - failed.length;
    if (done > 0) show({ tone: 'success', message: `${done} ${done === 1 ? 'person' : 'people'} selected`, detail: 'The recruiter, the requester and the line manager have been told.' });
    if (failed.length > 0) show({ tone: 'danger', message: `${failed.length} not selected`, detail: failed[0] });
  }

  async function bookExamSession(examSessionId: string, sessionName: string): Promise<void> {
    const people = toTest.length;
    const leftOut = selected.length - people;
    close();
    setSelection(EMPTY_SELECTION);
    try {
      // The pipeline's own "Add to exam session" (S18 Q2): into Assessment, then the seat and N-74.
      await moveToAssessment({ action: 'book_exam_session', selection: { kind: 'ids', ids: toTest.map((row) => row.applicationId) }, examSessionId });
      show({
        tone: 'success',
        message: `Booking ${people} ${people === 1 ? 'person' : 'people'} on ${sessionName}`,
        detail: `They move to Assessment, and each is emailed the date and place.${leftOut > 0 ? ` ${leftOut} not confirmed ${leftOut === 1 ? 'was' : 'were'} left out.` : ''}`,
      });
    } catch (error) {
      show({ tone: 'danger', message: 'Nobody was booked', detail: errorMessage(error) });
    }
  }

  /** CT-3: a call's outcome is one tap, always in view — a phone has no hover to reveal it. */
  const actionsOf = (row: ContactRow): ReactNode => {
    if (!view.canEdit) return null;
    const isOpen = isOpenContactStatus(row.contactStatus);
    const hasMoves = statusMovesFor(row.contactStatus, view.canMove).length > 0;
    // Anyone still open or Confirmed, and still at Contact; someone who said no or stopped answering isn't offered a next step.
    // Only for people who may move candidates on (an HR assistant works the list but never moves anyone, ADR 0085).
    const canSendOn = view.canMove && nextKind !== null && row.stage === 'screening' && (isOpen || row.contactStatus === 'confirmed');
    const blockedReason = nextKind === 'assessment' && view.sendAssessment ? view.sendAssessment.blockedReason : null;
    if (!isOpen && !hasMoves && !canSendOn) return '—';
    return (
      <span className={styles.rowActions}>
        {isOpen && (
          <>
            <Button size="sm" variant="ghost" icon={Phone} disabled={isPending(row.applicationId)} onClick={() => void log(row, 'reached')}>
              Reached
            </Button>
            <Button size="sm" variant="ghost" icon={PhoneMissed} disabled={isPending(row.applicationId)} onClick={() => void log(row, 'no_answer')}>
              No answer
            </Button>
          </>
        )}
        {hasMoves && (
          <Button size="sm" variant="ghost" icon={Tag} disabled={isPending(row.applicationId)} onClick={(event) => setPopped({ kind: 'status', row, anchor: anchorFrom(event.currentTarget) })}>
            Mark…
          </Button>
        )}
        {canSendOn && (
          <Button
            size="sm"
            variant="outline"
            icon={ArrowRight}
            disabled={isPending(row.applicationId) || sendingId !== null || blockedReason !== null}
            title={blockedReason ?? undefined}
            onClick={() => void sendOn(row)}
          >
            {nextKind === 'select' ? 'Select' : `Send to ${view.nextStepLabel}`}
          </Button>
        )}
      </span>
    );
  };

  const columns: readonly Column<ContactRow>[] = [
    {
      id: 'name',
      header: 'Candidate',
      isIdentity: true,
      defaultWidth: 220,
      exportValue: (row) => row.candidateName,
      cell: (row) => (
        <span className={styles.who}>
          <Avatar name={row.candidateName} />
          <CandidateName row={row} onOpen={onOpenCandidate} />
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Contact',
      defaultWidth: 190,
      exportValue: (row) => CONTACT_STATUS_LABELS[row.contactStatus],
      cell: (row) => (
        <span className={styles.statusCell}>
          <StatusChip row={row} isPending={isPending(row.applicationId)} />
          {row.isNoResponseSuggested && row.contactStatus !== 'no_response' && <span className={styles.hint}>No response?</span>}
        </span>
      ),
    },
    {
      id: 'phone',
      header: 'Phone',
      defaultWidth: 160,
      exportValue: (row) => row.phone,
      cell: (row) =>
        row.phone ? (
          <a className={styles.phone} href={`tel:${row.phone}`} onClick={(event) => event.stopPropagation()}>
            <Phone size={14} aria-hidden />
            {row.phone}
          </a>
        ) : (
          '—'
        ),
    },
    { id: 'call', header: 'Log a call', defaultWidth: 500, cell: actionsOf },
    {
      id: 'email',
      header: 'Last email',
      defaultWidth: 220,
      exportValue: (row) => (row.lastEmail ? `${row.lastEmail.templateName ?? row.lastEmail.subject} ${row.lastEmail.sentAt}` : null),
      cell: (row) =>
        row.lastEmail ? (
          <span className={styles.stack} title={row.lastEmail.subject}>
            <span>{row.lastEmail.templateName ?? row.lastEmail.subject}</span>
            <span className={styles.meta}>{formatDateTime(row.lastEmail.sentAt, { timeZone: zone })}</span>
          </span>
        ) : (
          '—'
        ),
    },
    {
      id: 'last',
      header: 'Last contact',
      defaultWidth: 130,
      exportValue: (row) => row.lastContactAt,
      cell: (row) => (row.lastContactAt ? <span title={formatDateTime(row.lastContactAt, { timeZone: zone })}>{formatRelative(row.lastContactAt)}</span> : '—'),
    },
    { id: 'attempts', header: 'Attempts', isNumeric: true, defaultWidth: 90, exportValue: (row) => row.attempts, cell: (row) => row.attempts },
    {
      id: 'next',
      header: 'Next step',
      defaultWidth: 180,
      exportValue: (row) => row.nextStep,
      cell: (row) => (
        <span className={styles.stack}>
          <span>{row.nextStep}</span>
          {row.interviewAt && <span className={styles.meta}>{formatDateTime(row.interviewAt, { timeZone: zone })}</span>}
        </span>
      ),
    },
  ];

  const countOf = (key: ContactFilter): number => view.rows.filter((row) => matchesContactFilter(row, key)).length;
  const emptyState = (
    <EmptyState
      title={filter === 'all' ? 'Nobody to contact yet' : `Nobody is ${CONTACT_FILTER_LABELS[filter].toLowerCase()}`}
      body={filter === 'all' ? 'When the manager says yes on the shortlist, or HR goes ahead without waiting, the person appears here.' : 'Try another filter, or Everyone.'}
      action={
        filter === 'all' ? (
          <Link to="/jobs/$jobId/$tab" params={{ jobId: detail.id, tab: 'shortlist' }} className={styles.action}>
            Open the shortlist
          </Link>
        ) : (
          <Button variant="outline" onClick={() => onFilter('all')}>
            Show everyone
          </Button>
        )
      }
      isInset
    />
  );

  return (
    <div className={`${styles.tracker} ${fit.fill}`}>
      <div className={styles.filters} role="group" aria-label="Show">
        {CONTACT_FILTERS.map((key) => (
          <button key={key} type="button" className={styles.filter} aria-pressed={filter === key} onClick={() => onFilter(key)}>
            {CONTACT_FILTER_LABELS[key]}
            <span className={styles.filterCount}>{countOf(key)}</span>
          </button>
        ))}
      </div>

      <div className={`${styles.table} ${fit.fill}`}>
        <DataTable
          screenId="contact"
          label="Contact tracker"
          columns={view.canEdit ? columns : columns.filter((column) => column.id !== 'call')}
          rows={rows}
          getRowId={(row) => row.applicationId}
          sort={[]}
          onSortChange={() => undefined}
          selection={selection}
          onSelectionChange={setSelection}
          isSelectable={view.canEdit}
          toneOf={(row) => CONTACT_STATUS_TONES[row.contactStatus]}
          emptyState={emptyState}
          bulkActions={
            <>
              <Button size="sm" variant="outline" icon={Mail} onClick={(event) => setPopped({ kind: 'email', anchor: anchorFrom(event.currentTarget) })}>
                Email from template
              </Button>
              {view.canMove && testAction === 'book_session' && (
                <ModuleGate module="exam_sessions">
                  <Button
                    size="sm"
                    variant="outline"
                    icon={CalendarPlus}
                    disabled={toTest.length === 0}
                    title={toTest.length === 0 ? 'Select people marked Confirmed.' : undefined}
                    onClick={(event) => setPopped({ kind: 'exam', anchor: anchorFrom(event.currentTarget) })}
                  >
                    Add to exam session{toTest.length > 0 ? ` (${toTest.length})` : ''}
                  </Button>
                </ModuleGate>
              )}
              {view.canMove && view.sendAssessment && (testAction === 'send_link' || testAction === 'move_only') && (
                <Button
                  size="sm"
                  variant="outline"
                  icon={ClipboardCheck}
                  disabled={view.sendAssessment.blockedReason !== null || toTest.length === 0}
                  title={view.sendAssessment.blockedReason ?? (toTest.length === 0 ? 'Select people marked Confirmed.' : undefined)}
                  onClick={() => void sendAssessment()}
                >
                  {testAction === 'move_only' ? 'Move to Assessment' : 'Send assessment'}
                  {toTest.length > 0 ? ` (${toTest.length})` : ''}
                </Button>
              )}
              {view.canMove && nextKind === 'interview' && (
                <Button
                  size="sm"
                  variant="outline"
                  icon={ArrowRight}
                  disabled={toTest.length === 0}
                  title={toTest.length === 0 ? 'Select people marked Confirmed.' : undefined}
                  onClick={() => void sendConfirmedToRound()}
                >
                  Send to {view.nextStepLabel}
                  {toTest.length > 0 ? ` (${toTest.length})` : ''}
                </Button>
              )}
              {view.canMove && view.selectFromHere && (
                <Button
                  size="sm"
                  variant="outline"
                  icon={Award}
                  disabled={view.selectFromHere.blockedReason !== null || toTest.length === 0}
                  title={view.selectFromHere.blockedReason ?? (toTest.length === 0 ? 'Select people marked Confirmed.' : undefined)}
                  onClick={() => void selectConfirmed()}
                >
                  Select{toTest.length > 0 ? ` (${toTest.length})` : ''}
                </Button>
              )}
            </>
          }
        />
      </div>

      {/* A phone gets one card per person: the number to tap, then the call's outcome (CT-3). */}
      <ul className={styles.cards} aria-label="Contact tracker">
        {rows.length === 0 && <li>{emptyState}</li>}
        {rows.map((row) => (
          <li key={row.applicationId} className={`${toneCardClass()} ${styles.card}`} data-tone={CONTACT_STATUS_TONES[row.contactStatus]}>
            <span className={styles.cardHead}>
              <span className={styles.who}>
                <Avatar name={row.candidateName} size="sm" />
                <CandidateName row={row} onOpen={onOpenCandidate} />
              </span>
              <StatusChip row={row} isPending={isPending(row.applicationId)} />
            </span>
            {row.phone && (
              <a className={styles.cardPhone} href={`tel:${row.phone}`}>
                <Phone size={16} aria-hidden />
                {row.phone}
              </a>
            )}
            {actionsOf(row)}
            <span className={styles.meta}>
              {row.lastEmail ? `${row.lastEmail.templateName ?? row.lastEmail.subject} · ${formatDateTime(row.lastEmail.sentAt, { timeZone: zone })}` : 'No email yet'}
              {` · ${row.attempts} ${row.attempts === 1 ? 'attempt' : 'attempts'} · Next: ${row.nextStep}`}
            </span>
          </li>
        ))}
      </ul>

      <Popover anchor={popped?.anchor ?? null} onClose={close} label={popupLabel(popped)} align="right">
        {popped?.kind === 'status' && (
          <StatusMenu
            row={popped.row}
            canMove={view.canMove}
            onPick={(to) => {
              if (CONTACT_WITHDRAWAL_STATUSES.includes(to)) {
                setPopped({ kind: 'withdraw', row: popped.row, to, anchor: popped.anchor });
                return;
              }
              close();
              void set(popped.row, { to });
            }}
          />
        )}
        {popped?.kind === 'withdraw' && (
          <WithdrawalPicker
            applicationId={popped.row.applicationId}
            version={popped.row.version}
            candidateName={popped.row.candidateName}
            onDone={close}
            onConfirm={(choice) => void set(popped.row, { to: popped.to, withdrawalReasonCode: choice.reasonCode, note: choice.note })}
          />
        )}
        {popped?.kind === 'email' && (
          <ComposePanel recipientsLabel={`${selected.length} ${selected.length === 1 ? 'person' : 'people'} — each filled in with their own name and job`} onCancel={close} onDone={close} onSend={emailSelected} />
        )}
        {popped?.kind === 'exam' && (
          <BookSessionPanel
            jobId={detail.id}
            who={toTest.length === 1 ? (toTest[0]?.candidateName ?? '1 person') : `${toTest.length} people`}
            needed={toTest.length}
            onCancel={close}
            onNewSession={() => {
              close();
              setIsCreatingSession(true);
            }}
            onPick={(sessionId, sessionName) => void bookExamSession(sessionId, sessionName)}
          />
        )}
      </Popover>
      <SessionEditor isOpen={isCreatingSession} onClose={() => setIsCreatingSession(false)} session={null} jobId={detail.id} />
      <TrackerPopup rows={rows} everyone={view.rows} openApplicationId={openApplicationId} tab={panelTab} onTabChange={setPanelTab} onOpen={onOpenCandidate} />
    </div>
  );
}

/** The name opens the candidate pop-up, as on All candidates (Job-contact.dc.html "Candidate"). */
function CandidateName({ row, onOpen }: { readonly row: ContactRow; readonly onOpen: (applicationId: string) => void }): ReactNode {
  return (
    <button
      type="button"
      className={styles.nameButton}
      onClick={(event) => {
        event.stopPropagation();
        onOpen(row.applicationId);
      }}
    >
      {row.candidateName}
    </button>
  );
}

/**
 * The status a person just picked shows at once (§5a rule 1); while the server confirms, only a
 * small mark beside it says so (rule 4) — never "Saving…" in place of the status.
 */
function StatusChip({ row, isPending }: { readonly row: ContactRow; readonly isPending: boolean }): ReactNode {
  return (
    <span className={styles.statusChip} aria-busy={isPending || undefined}>
      <Chip tone={CONTACT_STATUS_TONES[row.contactStatus]} plain={row.contactStatus !== 'no_answer'}>
        {CONTACT_STATUS_LABELS[row.contactStatus]}
      </Chip>
      {isPending && <span className={styles.pendingMark} aria-hidden />}
    </span>
  );
}

/** The pop-up over the tracker, stepping through the people the filter shows. */
function TrackerPopup({
  rows,
  everyone,
  openApplicationId,
  tab,
  onTabChange,
  onOpen,
}: {
  readonly rows: readonly ContactRow[];
  /** A person the filter no longer shows (just marked Reached on "Not contacted") stays open. */
  readonly everyone: readonly ContactRow[];
  readonly openApplicationId: string | null;
  readonly tab: CandidatePanelTab;
  readonly onTabChange: (tab: CandidatePanelTab) => void;
  readonly onOpen: (applicationId: string | null) => void;
}): ReactNode {
  const index = rows.findIndex((row) => row.applicationId === openApplicationId);
  const row = rows[index] ?? everyone.find((person) => person.applicationId === openApplicationId);
  if (!row) return null;
  const previous = index > 0 ? rows[index - 1] : undefined;
  const next = index >= 0 ? rows[index + 1] : undefined;
  return (
    <CandidatePopup
      candidateId={row.candidateId}
      tab={tab}
      onTabChange={onTabChange}
      onClose={() => onOpen(null)}
      {...(index >= 0
        ? {
            nav: {
              position: index + 1,
              total: rows.length,
              onPrevious: previous ? () => onOpen(previous.applicationId) : null,
              onNext: next ? () => onOpen(next.applicationId) : null,
            },
          }
        : {})}
    />
  );
}

function popupLabel(popped: Popped | null): string {
  if (popped?.kind === 'email') return 'Email from template';
  if (popped?.kind === 'exam') return 'Book a session';
  return 'Mark contact status';
}

/**
 * The moves §L25 allows from here. ADR 0085: Not interested and Not coming take someone off the
 * job, so they are offered only to whoever may move people on (`canMove`), never an HR assistant.
 */
function statusMovesFor(status: ContactRow['contactStatus'], canMove: boolean): readonly ContactManualStatus[] {
  return contactMovesFrom(status).filter((to) => canMove || !CONTACT_WITHDRAWAL_STATUSES.includes(to));
}

/** The moves in plain words; Not interested and Not coming ask for the reason next. */
function StatusMenu({ row, canMove, onPick }: { readonly row: ContactRow; readonly canMove: boolean; readonly onPick: (to: ContactManualStatus) => void }): ReactNode {
  return (
    <div className={styles.menu} role="group" aria-label={`Mark ${row.candidateName}`}>
      {statusMovesFor(row.contactStatus, canMove).map((to) => (
        <MenuItem
          key={to}
          {...(CONTACT_WITHDRAWAL_STATUSES.includes(to) ? { icon: <PhoneOff size={15} /> } : {})}
          isDanger={CONTACT_WITHDRAWAL_STATUSES.includes(to)}
          onClick={() => onPick(to)}
        >
          {CONTACT_STATUS_LABELS[to]}
          {CONTACT_WITHDRAWAL_STATUSES.includes(to) ? '…' : ''}
        </MenuItem>
      ))}
    </div>
  );
}
